import { useCallback, useEffect, useState, type CSSProperties, type RefObject } from 'react';

/**
 * Scroll geometry for the in-chat thread dock: floating composer + capability strip
 * at the bottom, full-height message scroll with mask fades. See docs/DESIGN_SYSTEM.md
 * § Chat composer modes.
 */
export const CHAT_THREAD_NEAR_BOTTOM_PX = 96;
export const CHAT_THREAD_SCROLL_TOP_THRESHOLD_PX = 8;
/** Short fade at the top edge when scrolled into history. */
const TOP_FADE_PX = 16;
/** Fade band directly above the dock (capability pills / composer). */
const BOTTOM_FADE_PX = 96;
/** Right strip excluded from the fade so the scrollbar renders at full opacity. */
const SCROLLBAR_EXCLUDE_PX = 12;
/**
 * Viewport-relative breathing gap between the last message and the composer.
 * `svh` stays stable under mobile URL-bar collapse and keyboard viewport changes.
 */
export const CHAT_THREAD_BREATHING_GAP = 'clamp(48px, 20svh, 280px)';
const DEFAULT_DOCK_HEIGHT_PX = 120;

export type ChatThreadScrollEdgesOptions = {
  /** False on welcome empty-chat where the scroll container is not mounted. */
  active?: boolean;
};

/**
 * Mask making thread content fully transparent behind the dock (composer +
 * capability pills), with a short fade band above the dock and at the top
 * edge. Layer 2 keeps the scrollbar strip at full opacity. Verified pattern:
 * mask on a non-scrolling wrapper around the scrollport.
 */
function buildThreadMaskStyle(dockHeightPx: number, showTopFade: boolean): CSSProperties {
  const topFadePx = showTopFade ? TOP_FADE_PX : 0;
  const fade = `linear-gradient(to bottom, transparent 0, black ${topFadePx}px, black calc(100% - ${
    dockHeightPx + BOTTOM_FADE_PX
  }px), transparent calc(100% - ${dockHeightPx}px))`;
  const maskImage = `${fade}, linear-gradient(black, black)`;
  const maskSize = `calc(100% - ${SCROLLBAR_EXCLUDE_PX}px) 100%, ${SCROLLBAR_EXCLUDE_PX}px 100%`;
  const maskPosition = 'left top, right top';

  return {
    maskImage,
    maskSize,
    maskPosition,
    maskRepeat: 'no-repeat',
    WebkitMaskImage: maskImage,
    WebkitMaskSize: maskSize,
    WebkitMaskPosition: maskPosition,
    WebkitMaskRepeat: 'no-repeat',
  };
}

/**
 * Geometry for the full-height chat thread with a floating composer dock.
 *
 * Owns a single measured value — dock height (pane bottom → top of the
 * capability strip, or the input block when no strip) — and derives from it:
 * - `scrollPaddingBottom`: keeps the last turn above the dock plus breathing gap
 * - `threadMaskStyle`: fades content out above the dock and hides it behind
 *   the dock entirely, letting decorative page backgrounds show through
 */
export function useChatThreadScrollEdges(
  scrollRef: RefObject<HTMLDivElement | null>,
  composerBlockRef: RefObject<HTMLDivElement | null>,
  options: ChatThreadScrollEdgesOptions = {}
) {
  const { active = true } = options;
  const [dockHeightPx, setDockHeightPx] = useState(DEFAULT_DOCK_HEIGHT_PX);
  const [showTopFade, setShowTopFade] = useState(false);

  const measure = useCallback(() => {
    const scroll = scrollRef.current;
    const block = composerBlockRef.current;
    if (!scroll || !block) return;

    const scrollBottom = scroll.getBoundingClientRect().bottom;

    // Dock = input block only; the capability strip floats within the fade
    // band above it (pills are opaque, gaps show fading content behind them).
    const dockTop = block.getBoundingClientRect().top;

    // Never let the dock zone eat more than half the viewport.
    const maxDock = Math.max(BOTTOM_FADE_PX, Math.floor(scroll.clientHeight / 2));
    const nextDock = Math.min(Math.max(0, Math.ceil(scrollBottom - dockTop)), maxDock);
    setDockHeightPx((prev) => (prev === nextDock ? prev : nextDock));

    const canScroll = scroll.scrollHeight > scroll.clientHeight + 1;
    const nextTopFade = canScroll && scroll.scrollTop > CHAT_THREAD_SCROLL_TOP_THRESHOLD_PX;
    setShowTopFade((prev) => (prev === nextTopFade ? prev : nextTopFade));
  }, [scrollRef, composerBlockRef]);

  useEffect(() => {
    if (!active) {
      setShowTopFade(false);
      return;
    }

    measure();
    const scroll = scrollRef.current;
    const block = composerBlockRef.current;
    if (!scroll || !block) return;

    scroll.addEventListener('scroll', measure, { passive: true });

    const observer = new ResizeObserver(measure);
    observer.observe(scroll);
    observer.observe(block);
    const content = scroll.firstElementChild;
    if (content instanceof HTMLElement) observer.observe(content);

    return () => {
      scroll.removeEventListener('scroll', measure);
      observer.disconnect();
    };
  }, [active, scrollRef, composerBlockRef, measure]);

  return {
    scrollPaddingBottom: `calc(${dockHeightPx}px + ${CHAT_THREAD_BREATHING_GAP})`,
    threadMaskStyle: buildThreadMaskStyle(dockHeightPx, showTopFade),
  };
}
